import { useState, useEffect, useRef } from 'react';
import { Shield, AlertTriangle, Info, CheckCircle2, Car } from 'lucide-react';
import { supabase } from '@/lib/supabase';

const KOOP_ROT = '#C41E3A';

// OLG Hamm-Linie: Fracke = Mittelwert Schwacke + Fraunhofer
// Leitsatz OLG Hamm 9 U 142/15 vom 18.03.2016
const OLG_HAMM_AZ = 'OLG Hamm 9 U 142/15 (18.03.2016)';
const BGH_EIGENERSPARNIS_AZ = 'BGH VI ZR 32/05';

interface MietwagentarifRow {
  klasse_id: number;
  schwacke_tag_eur: number | null;
  fraunhofer_tag_eur: number | null;
  fracke_tag_eur: number | null;
}

export interface PreisSchrittValues {
  tagessatz: number;
  vollkasko: boolean;
  mietdauer_tage: number;
  reparaturzeit_tage: number;
  ist_totalschaden: boolean;
}

interface PreisSchrittProps {
  // Schwacke-Klassen-ID des gemieteten Fahrzeugs (aus fahrzeuge.mietwagen_klasse_id)
  fahrzeugKlasseId: number | null;
  // Schwacke-Klassen-ID des beschädigten Fahrzeugs des Geschädigten
  geschaedigtenKlasseId: number | null;
  // Ist es ein Sonderfahrzeug ohne Schwacke-Klasse (Wohnmobil, Adria, Transporter)?
  istSonderfahrzeug: boolean;
  values: PreisSchrittValues;
  onChange: (v: PreisSchrittValues) => void;
  onBack: () => void;
  onNext: () => void;
}

// Eigenersparnis-Logik gem. BGH VI ZR 32/05
export function berechneEigenersparnis(
  gewaehltKlasse: number,
  geschaedigtenKlasse: number | null,
): 'kein_abzug' | 'zehn_prozent' | 'warnung_hoeher' {
  if (geschaedigtenKlasse === null) return 'kein_abzug';
  if (gewaehltKlasse < geschaedigtenKlasse) return 'kein_abzug';
  if (gewaehltKlasse === geschaedigtenKlasse) return 'zehn_prozent';
  return 'warnung_hoeher';
}

// Mietdauer-Plausibilitätsprüfung gem. BGH VI ZR 363/11
export function pruefeMietdauer(
  mietdauerTage: number,
  reparaturzeitTage: number,
  istTotalschaden: boolean,
): 'ok' | 'hinweis_ueberlaeng' {
  const maxDauer = istTotalschaden ? 14 + 5 : reparaturzeitTage + 7;
  return mietdauerTage > maxDauer ? 'hinweis_ueberlaeng' : 'ok';
}

export default function PreisSchritt({
  fahrzeugKlasseId,
  geschaedigtenKlasseId,
  istSonderfahrzeug,
  values,
  onChange,
  onBack,
  onNext,
}: PreisSchrittProps) {
  const [tarif, setTarif] = useState<MietwagentarifRow | null>(null);
  const [loadingTarif, setLoadingTarif] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const vorToggleMietdauerRef = useRef<number>(values.mietdauer_tage);

  // Lade Fracke-Tarif für die gewählte Klasse + PLZ 58 (OLG Hamm-Bezirk)
  useEffect(() => {
    if (!fahrzeugKlasseId || istSonderfahrzeug) return;
    setLoadingTarif(true);
    (async () => {
      try {
        const { data } = await supabase
          .from('mietwagen_tarife')
          .select('klasse_id, schwacke_tag_eur, fraunhofer_tag_eur, fracke_tag_eur')
          .eq('klasse_id', fahrzeugKlasseId)
          .eq('plz_bereich', '58')
          .eq('ist_aktuell', true)
          .single();
        setTarif(data ?? null);
      } finally {
        setLoadingTarif(false);
      }
    })();
  }, [fahrzeugKlasseId, istSonderfahrzeug]);

  // Auto-Vorbelegen mit Fracke-Empfehlung sobald Tarif geladen und kein manueller Wert gesetzt
  useEffect(() => {
    if (tarif?.fracke_tag_eur && values.tagessatz === 0) {
      onChange({ ...values, tagessatz: Number(tarif.fracke_tag_eur) });
    }
  // Bewusst nur auf tarif-Änderung reagieren — values/onChange sind stabil wenn Klasse gewählt
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tarif]);

  const eigenersparnisStatus =
    fahrzeugKlasseId !== null
      ? berechneEigenersparnis(fahrzeugKlasseId, geschaedigtenKlasseId)
      : 'kein_abzug';

  const mietdauerStatus = pruefeMietdauer(
    values.mietdauer_tage,
    values.reparaturzeit_tage,
    values.ist_totalschaden,
  );

  const vollkaskoTagesPreis = 13.40;

  const nettomietpreis = values.tagessatz * values.mietdauer_tage;
  const eigenersparnisAbzug =
    eigenersparnisStatus === 'zehn_prozent' ? nettomietpreis * 0.1 : 0;
  const vollkaskoGesamt = values.vollkasko
    ? vollkaskoTagesPreis * values.mietdauer_tage
    : 0;
  const gesamtbetrag = nettomietpreis - eigenersparnisAbzug + vollkaskoGesamt;

  // Warngrenze: außerhalb Schwacke/Fraunhofer-Band
  const tagessatzUeberSchwacke =
    tarif?.schwacke_tag_eur != null && values.tagessatz > Number(tarif.schwacke_tag_eur);
  const tagessatzUnterFraunhofer =
    tarif?.fraunhofer_tag_eur != null && values.tagessatz < Number(tarif.fraunhofer_tag_eur);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-900 text-center">Konditionen & Preise</h2>

      {/* Sonderfahrzeug: Wohnmobil/Adria → kein Schwacke-Klassen-Mapping */}
      {istSonderfahrzeug && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Car size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-amber-800">Sonderfahrzeug / Wohnmobil / Adria</p>
              <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                Dieses Fahrzeug fällt nicht in die Schwacke-Klassen 1–11. Bitte geben Sie den
                ortsüblichen Marktpreis manuell ein (Quelle: Vergleichsangebote vor Ort).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sonderfahrzeug: Marktpreis-Eingabe (außerhalb des Klassen-Guards) */}
      {istSonderfahrzeug && (
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Marktpreis (€/Tag)</p>
          <input
            type="number"
            min={0}
            step={0.01}
            value={values.tagessatz || ''}
            onChange={e => onChange({ ...values, tagessatz: parseFloat(e.target.value) || 0 })}
            className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-red-300"
            placeholder="Marktpreis in €/Tag eingeben"
          />
          <p className="text-[10px] text-gray-400 mt-1">
            Ortsüblicher Marktpreis gem. Vergleichsangeboten vor Ort
          </p>
        </div>
      )}

      {/* Tarif-Empfehlungsbox (nur für Klassen-Fahrzeuge) */}
      {!istSonderfahrzeug && (
        <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Tagessatz-Empfehlung</p>
            <button
              onClick={() => setShowTooltip(!showTooltip)}
              className="text-gray-400 hover:text-gray-600"
              title={OLG_HAMM_AZ}
            >
              <Info size={16} />
            </button>
          </div>

          {/* Tooltip BGH-Aktenzeichen */}
          {showTooltip && (
            <div className="bg-gray-800 text-white text-xs rounded-lg px-3 py-2 leading-relaxed">
              <p className="font-bold mb-1">OLG Hamm 9 U 142/15 vom 18.03.2016</p>
              <p>Für den OLG Hamm-Bezirk (PLZ 58) gilt die Fracke-Methode: Mittelwert aus Schwacke
                und Fraunhofer. BGH-Position: Tatrichterwahlfreiheit (BGH VI ZR 300/09).</p>
            </div>
          )}

          {loadingTarif ? (
            <p className="text-xs text-gray-400 text-center py-2">Lade Tarife…</p>
          ) : tarif ? (
            <div className="grid grid-cols-3 gap-2">
              {/* Fraunhofer */}
              <button
                onClick={() => onChange({ ...values, tagessatz: Number(tarif.fraunhofer_tag_eur) })}
                className={`rounded-xl p-3 text-center border-2 transition-all ${
                  values.tagessatz === Number(tarif.fraunhofer_tag_eur)
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                }`}
              >
                <p className="text-[10px] font-semibold text-gray-400 uppercase">Fraunhofer</p>
                <p className="text-base font-bold text-gray-800 mt-0.5">
                  {Number(tarif.fraunhofer_tag_eur).toFixed(2)} €
                </p>
                <p className="text-[9px] text-gray-400">Minimum</p>
              </button>

              {/* Fracke — Empfehlung */}
              <button
                onClick={() => onChange({ ...values, tagessatz: Number(tarif.fracke_tag_eur) })}
                className={`rounded-xl p-3 text-center border-2 transition-all relative ${
                  values.tagessatz === Number(tarif.fracke_tag_eur)
                    ? 'border-emerald-400 bg-emerald-50'
                    : 'border-emerald-200 bg-emerald-50/50 hover:border-emerald-400'
                }`}
              >
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] bg-emerald-500 text-white px-2 py-0.5 rounded-full font-bold whitespace-nowrap">
                  OLG Hamm
                </span>
                <p className="text-[10px] font-semibold text-emerald-600 uppercase mt-1">Fracke</p>
                <p className="text-base font-bold text-emerald-700 mt-0.5">
                  {Number(tarif.fracke_tag_eur).toFixed(2)} €
                </p>
                <p className="text-[9px] text-emerald-600">Empfohlen</p>
              </button>

              {/* Schwacke */}
              <button
                onClick={() => onChange({ ...values, tagessatz: Number(tarif.schwacke_tag_eur) })}
                className={`rounded-xl p-3 text-center border-2 transition-all ${
                  values.tagessatz === Number(tarif.schwacke_tag_eur)
                    ? 'border-orange-400 bg-orange-50'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                }`}
              >
                <p className="text-[10px] font-semibold text-gray-400 uppercase">Schwacke</p>
                <p className="text-base font-bold text-gray-800 mt-0.5">
                  {Number(tarif.schwacke_tag_eur).toFixed(2)} €
                </p>
                <p className="text-[9px] text-gray-400">Maximum</p>
              </button>
            </div>
          ) : (
            <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
              Kein Tarif für diese Klasse gefunden — bitte Tagessatz manuell eingeben.
            </p>
          )}

          {/* Manueller Override */}
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Tagessatz (€/Tag)</p>
            <input
              type="number"
              min={0}
              step={0.01}
              value={values.tagessatz || ''}
              onChange={e => onChange({ ...values, tagessatz: parseFloat(e.target.value) || 0 })}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-red-300"
            />
          </div>

          {/* Warnungen außerhalb des Bandes */}
          {tagessatzUeberSchwacke && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-700">
                Tagessatz liegt über dem Schwacke-Wert — Versicherung wird kürzen.
              </p>
            </div>
          )}
          {tagessatzUnterFraunhofer && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <Info size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">
                Tagessatz liegt unter dem Fraunhofer-Wert — prüfen Sie nochmals.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Eigenersparnis */}
      {fahrzeugKlasseId !== null && (
        <div className={`rounded-xl px-4 py-3 border ${
          eigenersparnisStatus === 'warnung_hoeher'
            ? 'bg-red-50 border-red-200'
            : eigenersparnisStatus === 'zehn_prozent'
            ? 'bg-amber-50 border-amber-200'
            : 'bg-emerald-50 border-emerald-200'
        }`}>
          <div className="flex items-start gap-2">
            {eigenersparnisStatus === 'warnung_hoeher' ? (
              <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
            ) : (
              <Info size={14} className={`mt-0.5 flex-shrink-0 ${eigenersparnisStatus === 'zehn_prozent' ? 'text-amber-600' : 'text-emerald-600'}`} />
            )}
            <div>
              {eigenersparnisStatus === 'warnung_hoeher' && (
                <>
                  <p className="text-xs font-bold text-red-800">Höherwertige Klasse — nicht erstattungsfähig</p>
                  <p className="text-xs text-red-700 mt-0.5">
                    Mieter trägt Differenz zur gleichwertigen Klasse selbst.
                  </p>
                </>
              )}
              {eigenersparnisStatus === 'zehn_prozent' && (
                <>
                  <p className="text-xs font-bold text-amber-800">
                    Eigenersparnis: −10 % auf Nettomietpreis
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Klassengleiche Anmietung → Abzug −{eigenersparnisAbzug.toFixed(2)} € ({BGH_EIGENERSPARNIS_AZ})
                  </p>
                </>
              )}
              {eigenersparnisStatus === 'kein_abzug' && (
                <>
                  <p className="text-xs font-bold text-emerald-800">Klassen-Rückstufung — keine Eigenersparnis</p>
                  <p className="text-xs text-emerald-700 mt-0.5">
                    Günstigere Klasse als beschädigtes Fahrzeug → kein Abzug ({BGH_EIGENERSPARNIS_AZ})
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mietdauer + Reparaturzeit */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Mietdauer</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Mietdauer (Tage)</p>
            <input
              type="number"
              min={1}
              value={values.mietdauer_tage}
              onChange={e => onChange({ ...values, mietdauer_tage: parseInt(e.target.value) || 1 })}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-red-300"
            />
          </div>
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Reparaturzeit (Tage)</p>
            <input
              type="number"
              min={0}
              value={values.reparaturzeit_tage}
              onChange={e => onChange({ ...values, reparaturzeit_tage: parseInt(e.target.value) || 0 })}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-red-300"
            />
          </div>
        </div>

        {/* Totalschaden-Toggle */}
        <button
          onClick={() => {
            const isTotalschaden = !values.ist_totalschaden;
            if (isTotalschaden) {
              vorToggleMietdauerRef.current = values.mietdauer_tage;
              onChange({ ...values, ist_totalschaden: true, mietdauer_tage: 19 });
            } else {
              onChange({ ...values, ist_totalschaden: false, mietdauer_tage: vorToggleMietdauerRef.current });
            }
          }}
          className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
            values.ist_totalschaden ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
          }`}
        >
          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
            values.ist_totalschaden ? 'border-red-500 bg-red-500' : 'border-gray-300'
          }`}>
            {values.ist_totalschaden && <CheckCircle2 size={12} className="text-white" />}
          </div>
          <span className="text-xs text-gray-700">Totalschaden (14 + 5 Überlegungstage)</span>
        </button>

        {/* Mietdauer-Plausibilitätshinweis */}
        {mietdauerStatus === 'hinweis_ueberlaeng' && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700 leading-relaxed">
              Mietdauer überschreitet Reparaturzeit + Überlegungszeit deutlich — Versicherung
              kann kürzen (BGH VI ZR 363/11).
            </p>
          </div>
        )}
      </div>

      {/* Vollkasko-Switch */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        <button
          onClick={() => onChange({ ...values, vollkasko: !values.vollkasko })}
          className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
            values.vollkasko ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200'
          }`}
        >
          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
            values.vollkasko ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300'
          }`}>
            {values.vollkasko && <CheckCircle2 size={12} className="text-white" />}
          </div>
          <div className="flex-1">
            <p className="text-xs font-bold text-gray-800">Vollkasko-Schutz SB 500 € — {vollkaskoTagesPreis.toFixed(2)} €/Tag</p>
            <p className="text-[10px] text-gray-500">Immer erstattungsfähig (st. BGH-Rspr.)</p>
          </div>
          <Shield size={16} className={values.vollkasko ? 'text-emerald-500' : 'text-gray-300'} />
        </button>
        {!values.vollkasko && (
          <div className="mt-2 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700">Mieter trägt volle Haftung bei Schäden am Fahrzeug.</p>
          </div>
        )}
      </div>

      {/* Zusammenfassung */}
      <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4 space-y-2">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Kostenübersicht (Netto)</p>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">
            Mietpreis ({values.mietdauer_tage} Tage × {values.tagessatz.toFixed(2)} €)
          </span>
          <span className="font-semibold text-gray-800">{nettomietpreis.toFixed(2)} €</span>
        </div>
        {eigenersparnisAbzug > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-amber-600">− Eigenersparnis (10 %)</span>
            <span className="font-semibold text-amber-700">−{eigenersparnisAbzug.toFixed(2)} €</span>
          </div>
        )}
        {values.vollkasko && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Vollkasko ({values.mietdauer_tage} × {vollkaskoTagesPreis.toFixed(2)} €)</span>
            <span className="font-semibold text-gray-800">{vollkaskoGesamt.toFixed(2)} €</span>
          </div>
        )}
        <div className="border-t border-gray-200 pt-2 flex justify-between">
          <span className="text-sm font-bold text-gray-800">Gesamt (Netto)</span>
          <span className="text-sm font-bold" style={{ color: KOOP_ROT }}>{gesamtbetrag.toFixed(2)} €</span>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="flex-1 py-3.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all">
          Zurück
        </button>
        <button
          onClick={onNext}
          disabled={values.tagessatz <= 0 || eigenersparnisStatus === 'warnung_hoeher'}
          className="flex-1 py-3.5 rounded-xl text-white font-bold text-sm shadow-md hover:opacity-90 transition-all disabled:opacity-40"
          style={{ background: `linear-gradient(135deg, ${KOOP_ROT}, #9B1627)` }}
        >
          Zum Vertrag
        </button>
      </div>
    </div>
  );
}
